import React, { memo, useMemo, useCallback } from 'react';
import Select, { components } from 'react-select';
import { List } from 'react-window';

/**
 * Composant Select avec recherche et virtualisation
 * Utilise react-select + react-window pour gérer 80+ options sans lag
 *
 * Refactorisé le 02/01/2026:
 * - Remplace react-select-virtualized (non maintenu) par react-window
 * - Économie de ~11.5 KB gzip
 *
 * Mis à jour le 10/01/2026:
 * - Migration vers react-window v2 API
 *
 * Props:
 * - grouped: true si les options sont groupées (format: [{ label: 'Group', options: [...] }])
 */

const OPTION_HEIGHT = 40;
const MAX_VISIBLE_OPTIONS = 8;

/**
 * Composant Row pour la liste virtualisée (react-window v2)
 */
const OptionRow = memo(({ index, style, children }) => {
  const child = children[index];
  return (
    <div style={{ ...style, overflow: 'hidden' }}>
      {child}
    </div>
  );
});

OptionRow.displayName = 'OptionRow';

/**
 * MenuList virtualisé avec react-window v2
 */
const VirtualizedMenuList = ({ children, maxHeight }) => {
  const childrenArray = React.Children.toArray(children);
  const height = Math.min(
    maxHeight,
    childrenArray.length * OPTION_HEIGHT
  );

  if (childrenArray.length === 0) {
    return <div style={{ padding: '12px', color: '#9ca3af', textAlign: 'center' }}>Aucun résultat</div>;
  }

  // Si moins de 20 options, pas besoin de virtualiser
  if (childrenArray.length < 20) {
    return <div style={{ maxHeight, overflowY: 'auto' }}>{children}</div>;
  }

  // Props passées aux lignes virtualisées (react-window v2)
  const rowProps = useMemo(() => ({
    children: childrenArray
  }), [childrenArray]);

  return (
    <List
      style={{ height, width: '100%' }}
      rowCount={childrenArray.length}
      rowHeight={OPTION_HEIGHT}
      rowComponent={OptionRow}
      rowProps={rowProps}
    />
  );
};

const SearchableSelect = ({
  name,
  value,
  options,
  onChange,
  placeholder = 'Sélectionner...',
  isClearable = true,
  isDisabled = false,
  required = false,
  noOptionsMessage = 'Aucun résultat',
  formatOptionLabel,
  menuPortalTarget,
  grouped = false,
  // Props d'accessibilité
  id,
  ariaLabel,
  ariaLabelledBy
}) => {
  // Détecter automatiquement si les options sont groupées
  const isGrouped = useMemo(() => {
    if (grouped) return true;
    return options?.length > 0 && options[0]?.options !== undefined;
  }, [options, grouped]);

  // Trouver l'option sélectionnée (gère les options groupées et plates)
  const selectedOption = useMemo(() => {
    if (!value || !options) return null;

    for (const opt of options) {
      // Si c'est un groupe (a une propriété 'options')
      if (opt.options) {
        const found = opt.options.find(o => o.value === value);
        if (found) return found;
      } else if (opt.value === value) {
        return opt;
      }
    }
    return null;
  }, [options, value]);

  // Handler pour transformer la valeur react-select en événement standard
  const handleChange = useCallback((selected) => {
    // Simuler un événement pour compatibilité avec handleChange existant
    const syntheticEvent = {
      target: {
        name,
        value: selected ? selected.value : ''
      }
    };
    onChange(syntheticEvent);
  }, [name, onChange]);

  // Composants personnalisés pour react-select
  const customComponents = useMemo(() => ({
    MenuList: (props) => (
      <VirtualizedMenuList {...props} maxHeight={OPTION_HEIGHT * MAX_VISIBLE_OPTIONS} />
    )
  }), []);

  // Styles personnalisés pour react-select
  const customStyles = useMemo(() => ({
    control: (base, state) => ({
      ...base,
      minHeight: '44px',
      padding: '2px 4px',
      border: state.isFocused ? '2px solid #667eea' : '2px solid #e1e5e9',
      borderRadius: '10px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.15)' : 'none',
      fontSize: 'clamp(14px, 2.5vw, 16px)',
      fontFamily: 'inherit',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: state.isFocused ? '#667eea' : '#c5cad0'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 8px'
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9ca3af'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#374151'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '10px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      border: '1px solid #e1e5e9',
      overflow: 'hidden',
      zIndex: 9999
    }),
    menuList: (base) => ({
      ...base,
      padding: '4px'
    }),
    option: (base, state) => ({
      ...base,
      padding: '10px 12px',
      borderRadius: '6px',
      marginBottom: '2px',
      cursor: 'pointer',
      backgroundColor: state.isSelected
        ? '#667eea'
        : state.isFocused
          ? '#f0f4ff'
          : 'transparent',
      color: state.isSelected ? 'white' : '#374151',
      fontSize: 'clamp(13px, 2.5vw, 15px)',
      height: `${OPTION_HEIGHT}px`,
      display: 'flex',
      alignItems: 'center',
      '&:active': {
        backgroundColor: state.isSelected ? '#667eea' : '#e5e9ff'
      }
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: '#9ca3af',
      padding: '12px'
    }),
    group: (base) => ({
      ...base,
      paddingTop: '8px',
      paddingBottom: '4px'
    }),
    groupHeading: (base) => ({
      ...base,
      color: '#667eea',
      fontWeight: '600',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '4px',
      paddingLeft: '12px'
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '4px',
      cursor: 'pointer',
      color: '#9ca3af',
      '&:hover': {
        color: '#667eea'
      }
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      padding: '4px 8px',
      color: '#9ca3af',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
      transition: 'transform 0.2s ease',
      '&:hover': {
        color: '#667eea'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999
    })
  }), []);

  return (
    <Select
      name={name}
      inputId={id || `select-${name}`}
      value={selectedOption}
      options={options}
      onChange={handleChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={true}
      styles={customStyles}
      components={customComponents}
      noOptionsMessage={() => noOptionsMessage}
      formatOptionLabel={formatOptionLabel}
      menuPortalTarget={menuPortalTarget || document.body}
      menuPosition="fixed"
      classNamePrefix="searchable-select"
      aria-label={ariaLabel || placeholder}
      aria-labelledby={ariaLabelledBy}
      aria-required={required}
    />
  );
};

export default memo(SearchableSelect);
